import { Injectable, signal } from '@angular/core';
import { PaletteCommand } from './command-palette.model';

const RECENT_KEY = 'em-fe:command-palette:recent';
const RECENT_LIMIT = 5;

/**
 * Command palette (Ctrl/⌘ K) state. Each part of the app registers a provider of its own commands,
 * built with its own visibility and permission rules, so the palette never offers what the UI hides.
 */
@Injectable({
  providedIn: 'root',
})
export class CommandPaletteService {
  readonly isOpen = signal(false);

  private readonly providers = new Set<() => PaletteCommand[]>();

  open(): void {
    this.isOpen.set(true);
  }

  close(): void {
    this.isOpen.set(false);
  }

  toggle(): void {
    this.isOpen.update((open) => !open);
  }

  /** Adds a source of commands, read each time the palette opens. Returns the function that removes it. */
  register(provider: () => PaletteCommand[]): () => void {
    this.providers.add(provider);
    return () => this.providers.delete(provider);
  }

  commands(): PaletteCommand[] {
    return [...this.providers].flatMap((provider) => provider());
  }

  /** Ids of the last commands run, most recent first. */
  recentIds(): string[] {
    try {
      const parsed = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]');
      return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string').slice(0, RECENT_LIMIT) : [];
    } catch {
      return [];
    }
  }

  remember(id: string): void {
    const recent = [id, ...this.recentIds().filter((other) => other !== id)].slice(0, RECENT_LIMIT);
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(recent));
    } catch { /* storage may be unavailable: "Recent" just stays empty */ }
  }
}
